import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { searchExplore, SEARCH_MIN_CHARS } from "../../../Services/explore";
import { useStandaloneExploreHeader } from "../areaShared";
import { resolveLifeArea, useLifeAreas } from "../lifeAreas";
import LifeAreaIcon from "../sections/LifeAreaIcon";
import LifeAreaTile from "../sections/LifeAreaTile";
import chevronIcon from "../../../Assets/icon/explore/chevron-right.svg";
import closeIcon from "../../../Assets/icon/explore/close.svg";
import arrowLeftIcon from "../../../Assets/icon/explore/arrow-left.svg";
import {
  SEARCH_TAB,
  SEARCH_TABS,
  SEE_ALL_LIMIT,
  TOP_RESULTS,
  dialogueRoute,
  familyOf,
  lifeAreaRoute,
  normaliseSearch,
  personalSeeAllRoute,
  resultMeta,
} from "./searchModel";
import "./search.css";

/** Wait this long after the last keystroke before searching. */
const DEBOUNCE_MS = 350;

/**
 * Explore Search (aris-ai plan 47; design: search results screen).
 *
 * The query lives in the URL (`?q=`, `?family=` for a Collection drill-in, `?tab=`), so a
 * result → back returns to the same results. Search never starts a conversation: a
 * Dialogue opens its Overview, where Start / Continue happens. Lists render in the
 * order the server sent (AGS order) and are never re-sorted here.
 */
function SearchPage() {
  useStandaloneExploreHeader();
  useLifeAreas();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const family = params.get("family") || "";
  const tab = params.get("tab") || SEARCH_TAB.ALL;

  const [text, setText] = useState(q);
  const [state, setState] = useState({ loading: false, error: false, data: null });
  const inputRef = useRef(null);

  useEffect(() => setText(q), [q]);
  useEffect(() => inputRef.current?.focus(), []);

  const update = (next) => {
    const merged = new URLSearchParams(params);
    Object.entries(next).forEach(([key, value]) => {
      if (value) merged.set(key, value);
      else merged.delete(key);
    });
    setParams(merged, { replace: true });
  };

  // Typing updates the URL after a pause; the URL drives the request.
  useEffect(() => {
    if (text === q) return undefined;
    const timer = setTimeout(() => update({ q: text.trim(), family: "" }), DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const seeAll = tab === SEARCH_TAB.DIALOGUES;
  useEffect(() => {
    if (q.trim().length < SEARCH_MIN_CHARS) {
      setState({ loading: false, error: false, data: null });
      return undefined;
    }
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: false }));
    searchExplore(q.trim(), {
      family: family || undefined,
      limit: seeAll ? SEE_ALL_LIMIT : undefined,
      signal: controller.signal,
    })
      .then((data) =>
        setState({ loading: false, error: false, data: normaliseSearch(data) })
      )
      .catch((e) => {
        if (controller.signal.aborted) return;
        console.error("Explore search failed", e);
        setState({ loading: false, error: true, data: null });
      });
    return () => controller.abort();
  }, [q, family, seeAll]);

  const runSearch = (query) => update({ q: query, family: "", tab: "" });
  const go = (route) => route && navigate(route);
  const data = state.data;
  const hasQuery = q.trim().length >= SEARCH_MIN_CHARS;

  return (
    <div className="ex-search">
      <div className="ex-search__bar">
        <button
          type="button"
          className="ex-search__back"
          aria-label="Back"
          onClick={() => navigate("/explore")}
        >
          <img src={arrowLeftIcon} alt="" width="24" height="24" />
        </button>
        <form
          className="ex-search__field"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            runSearch(text.trim());
          }}
        >
          <span className="ex-search__icon" aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            enterKeyHint="search"
            aria-label="Search Dialogues"
            placeholder="What would you like help with today?"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          {text && (
            <button
              type="button"
              className="ex-search__clear"
              aria-label="Clear search"
              onClick={() => {
                setText("");
                runSearch("");
              }}
            >
              <img src={closeIcon} alt="" width="16" height="16" />
            </button>
          )}
        </form>
      </div>

      {hasQuery && (
        <div className="ex-search__tabs" role="tablist">
          {SEARCH_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={`ex-search__tab${tab === t.key ? " is-active" : ""}`}
              onClick={() => update({ tab: t.key === SEARCH_TAB.ALL ? "" : t.key })}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {family && (
        <div className="ex-search__family">
          <span>{family}</span>
          <button type="button" onClick={() => update({ family: "" })}>
            Show all results
          </button>
        </div>
      )}

      {!hasQuery && (
        <p className="ex-search__hint">
          Describe what&apos;s going on in your own words — for example &ldquo;my
          teenager won&apos;t talk to me&rdquo; or &ldquo;burnout&rdquo;.
        </p>
      )}
      {hasQuery && state.loading && !data && (
        <div className="ex-search__status">Searching…</div>
      )}
      {hasQuery && state.error && (
        <div className="ex-search__status">
          We couldn&apos;t search just now. Please try again.
        </div>
      )}

      {hasQuery && data && (
        <SearchResults
          data={data}
          tab={tab}
          query={q}
          onOpen={(item) => go(dialogueRoute(item))}
          onOpenArea={(area) => go(lifeAreaRoute(area))}
          onOpenCollection={(c) => update({ family: familyOf(c), tab: "" })}
          onSeeAll={() => update({ tab: SEARCH_TAB.DIALOGUES })}
          onSeeAllPersonal={() => navigate(personalSeeAllRoute(q))}
          onRelated={runSearch}
          onTalk={() => navigate("/chat")}
        />
      )}
    </div>
  );
}

function SearchResults({
  data,
  tab,
  query,
  onOpen,
  onOpenArea,
  onOpenCollection,
  onSeeAll,
  onSeeAllPersonal,
  onRelated,
  onTalk,
}) {
  if (tab === SEARCH_TAB.DIALOGUES) {
    return (
      <>
        <DialogueList items={data.dialogues} onOpen={onOpen} />
        {data.dialogues.length === 0 && <TalkWithAris onTalk={onTalk} />}
      </>
    );
  }
  if (tab === SEARCH_TAB.LIFE_AREAS) {
    return <LifeAreaList areas={data.lifeAreas} onOpen={onOpenArea} />;
  }
  if (tab === SEARCH_TAB.COLLECTIONS) {
    return <CollectionList items={data.collections} onOpen={onOpenCollection} />;
  }
  const top = data.dialogues.slice(0, TOP_RESULTS);
  return (
    <>
      {top.length > 0 && (
        <section className="ex-search__section">
          <div className="ex-search__head">
            <h2>Top results for &ldquo;{query}&rdquo;</h2>
            {data.dialogues.length > TOP_RESULTS && (
              <button type="button" className="ex-search__seeall" onClick={onSeeAll}>
                See All
              </button>
            )}
          </div>
          <DialogueList items={top} onOpen={onOpen} />
        </section>
      )}
      {data.personal.items.length > 0 && (
        <section className="ex-search__section">
          {/* DCI v27: the member's own Personal Dialogues are a separate group, up
              to three, with See All to their library. */}
          <div className="ex-search__head">
            <h2>Your Personal Dialogues</h2>
            {data.personal.total > data.personal.items.length && (
              <button type="button" className="ex-search__seeall" onClick={onSeeAllPersonal}>
                See All
              </button>
            )}
          </div>
          <DialogueList items={data.personal.items} onOpen={onOpen} />
        </section>
      )}
      <TalkWithAris onTalk={onTalk} />
      {data.related.length > 0 && (
        <section className="ex-search__section">
          <h2 className="ex-search__subhead">Related searches</h2>
          <ul className="ex-search__related">
            {data.related.map((s) => (
              <li key={s}>
                <button type="button" onClick={() => onRelated(s)}>
                  <span className="ex-search__icon" aria-hidden="true" />
                  <span className="ex-search__related-text">{s}</span>
                  <img src={chevronIcon} alt="" width="20" height="20" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}

function DialogueList({ items, onOpen }) {
  if (!items.length) {
    return <p className="ex-search__empty">No Dialogues matched yet.</p>;
  }
  return (
    <ul className="ex-search__list">
      {items.map((item) => (
        <li key={`${item.type || "research"}:${item.dialogue_id}`}>
          <ResultCard item={item} onOpen={onOpen} />
        </li>
      ))}
    </ul>
  );
}

function ResultCard({ item, onOpen }) {
  const area = item.display_area_key
    ? resolveLifeArea({ key: item.display_area_key })
    : null;
  const [imageOk, setImageOk] = useState(true);
  const showImage = Boolean(item.image_url) && imageOk;
  return (
    <button type="button" className="ex-search__card" onClick={() => onOpen(item)}>
      <span
        className="ex-search__art"
        style={!showImage && area ? { background: area.color } : undefined}
      >
        {showImage ? (
          <img src={item.image_url} alt="" onError={() => setImageOk(false)} />
        ) : (
          area && <LifeAreaIcon icon={area.icon} />
        )}
      </span>
      <span className="ex-search__body">
        <span className="ex-search__title">{item.title}</span>
        {item.description && (
          <span className="ex-search__desc">{item.description}</span>
        )}
        {resultMeta(item) && <span className="ex-search__meta">{resultMeta(item)}</span>}
      </span>
      <img className="ex-search__chev" src={chevronIcon} alt="" width="20" height="20" />
    </button>
  );
}

function LifeAreaList({ areas, onOpen }) {
  if (!areas.length) return <p className="ex-search__empty">No Life Areas matched.</p>;
  return (
    <ul className="ex-search__list">
      {areas.map((a) => {
        const area = resolveLifeArea({ key: a.key });
        return (
          <li key={a.key}>
            <button type="button" className="ex-search__row" onClick={() => onOpen(a)}>
              <LifeAreaTile area={area} className="ex-search__tile" />
              <span className="ex-search__body">
                <span className="ex-search__title">{a.title || a.label || area.title}</span>
                {a.subtitle && <span className="ex-search__desc">{a.subtitle}</span>}
              </span>
              <img src={chevronIcon} alt="" width="20" height="20" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function CollectionList({ items, onOpen }) {
  if (!items.length) return <p className="ex-search__empty">No Collections matched.</p>;
  return (
    <ul className="ex-search__list">
      {items.map((c) => (
        <li key={c.key}>
          <button type="button" className="ex-search__row" onClick={() => onOpen(c)}>
            <span className="ex-search__body">
              <span className="ex-search__title">{c.title}</span>
              <span className="ex-search__meta">
                {[c.display_area_label, c.dialogue_count && `${c.dialogue_count} Dialogues`]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </span>
            <img src={chevronIcon} alt="" width="20" height="20" />
          </button>
        </li>
      ))}
    </ul>
  );
}

function TalkWithAris({ onTalk }) {
  return (
    <section className="ex-search__talk">
      <span className="ex-search__talk-icon" aria-hidden="true">
        …
      </span>
      <div>
        <h3>Don&apos;t see what you&apos;re looking for?</h3>
        <p>
          Talk this through with Aris. Share a few more details and we&apos;ll help you
          find the right support.
        </p>
        <button type="button" onClick={onTalk}>
          Talk with Aris
        </button>
      </div>
    </section>
  );
}

export default SearchPage;
